/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2024 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2024 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */

import type { FC } from 'react';
import { memo, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import type { TimeSeriesService } from '@opengeoweb/shared';
import { TimeSeriesSelectList } from './TimeSeriesSelectList';
import { getCollectionKey } from './utils';
import {
  getAllFilterChipsEnabled,
  getSearchFilter,
  getServiceFilterChips,
  getCollectionSelection,
} from '../../store/selectors';
import type { TimeSeriesRootState } from '../../store/types';
import { useGetServiceCollectionDetails } from '../../utils/timeseries-api/hooks';
import type { Parameter, Plot } from '../TimeSeries/types';

interface TimeSeriesSelectListConnectProps {
  selectPlot: Plot;
  handleAddOrRemoveClick: (
    parameter: Parameter,
    plotHasParameter: boolean,
  ) => void;
  services: TimeSeriesService[];
  isOpen: boolean;
  panelId: string;
}

export const TimeSeriesSelectListConnect: FC<TimeSeriesSelectListConnectProps> =
  memo(
    ({
      selectPlot,
      handleAddOrRemoveClick,
      services,
      isOpen,
      panelId,
    }: TimeSeriesSelectListConnectProps) => {
      const { data: collectionList } = useGetServiceCollectionDetails(
        services,
        isOpen,
      );
      const [collectionListItemDetails, setCollectionListItemDetails] =
        useState<Record<string, boolean>>({});
      const collectionSelection = useSelector((store: TimeSeriesRootState) =>
        getCollectionSelection(store, panelId),
      );

      useEffect(() => {
        if (isOpen && collectionSelection) {
          setCollectionListItemDetails({
            [getCollectionKey(collectionSelection)]: true,
          });
        }
      }, [collectionSelection, isOpen]);

      const filterChipsServices = useSelector((store: TimeSeriesRootState) =>
        getServiceFilterChips(store, panelId),
      );
      const allFilterChipsEnabled = useSelector((store: TimeSeriesRootState) =>
        getAllFilterChipsEnabled(store, panelId),
      );

      const searchFilter = useSelector((state: TimeSeriesRootState) =>
        getSearchFilter(state, panelId),
      );

      return (
        <TimeSeriesSelectList
          allFilterChipsEnabled={allFilterChipsEnabled}
          collectionList={collectionList}
          collectionListItemDetails={collectionListItemDetails}
          collectionSelection={collectionSelection}
          filterChipsServices={filterChipsServices}
          handleAddOrRemoveClick={handleAddOrRemoveClick}
          panelId={panelId}
          searchFilter={searchFilter}
          selectPlot={selectPlot}
          setCollectionListItemDetails={setCollectionListItemDetails}
        />
      );
    },
  );
